'use client';

// ═══════════════════════════════════════════════════════════════════════════
// POSTHOG — réactivé le 24/09/2026, gated par consentement OPT-IN
// ═══════════════════════════════════════════════════════════════════════════
//
// Désactivé le 2026-06-18 parce que le chargement était inconditionnel : la
// politique de confidentialité (app/legal/privacy §8) promettait « aucun
// cookie de tracking » alors que PostHog en posait un dès le premier rendu,
// sans consentement — violation ePrivacy 2002/58/CE Art. 5(3).
//
// Ce qui change ici, et qui rend la réactivation conforme : le MODULE
// posthog-js n'est plus jamais importé statiquement. Il n'est chargé (import
// dynamique) qu'APRÈS que app/lib/cookie-consent.js rapporte un consentement
// 'accepted' — donc aucun cookie non essentiel n'est posé avant que le
// visiteur ait cliqué « Accepter » dans app/components/CookieBanner.js. Un
// refus explicite (ou un changement d'avis après acceptation) appelle
// opt_out_capturing() + reset() pour effacer les identifiants déjà posés.
//
// Politique de confidentialité §8 réécrite en conséquence (les 4 langues).

import { useEffect, useRef } from 'react';
import { getConsent, CONSENT_EVENT } from '@/app/lib/cookie-consent';

// Instance partagée au niveau module : évite un double posthog.init() si
// plusieurs pages montent ce provider (navigation client) ou si l'évènement
// de consentement se déclenche deux fois.
let posthogClient = null;
let loadingPromise = null;

async function ensureLoaded() {
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return null; // dev local sans .env, ou instance qui n'a pas activé PostHog — no-op silencieux
  if (posthogClient) return posthogClient;
  if (!loadingPromise) {
    loadingPromise = import('posthog-js').then(({ default: posthog }) => {
      posthog.init(key, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: true,
        autocapture: true,
      });
      posthogClient = posthog;
      return posthog;
    }).catch(() => null);
  }
  return loadingPromise;
}

function withdraw() {
  if (!posthogClient) return;
  try {
    posthogClient.opt_out_capturing();
    posthogClient.reset();
  } catch { /* best-effort — ne jamais faire planter l'app sur un retrait de consentement */ }
}

export default function PostHogProvider({ children }) {
  const initialised = useRef(false);

  useEffect(() => {
    if (getConsent() === 'accepted' && !initialised.current) {
      initialised.current = true;
      ensureLoaded();
    }
    const onChange = (e) => {
      const consent = e?.detail?.consent;
      if (consent === 'accepted') {
        initialised.current = true;
        ensureLoaded();
      } else if (consent === 'refused') {
        withdraw();
      }
    };
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  return children;
}
